/**
 * FlowSheet — the v2 bottom sheet. Ported from the "OnFlow Design System"
 * project (v2/components/core/FlowSheet.jsx).
 *
 * 28px top corners, grab handle, blurred scrim, slide-up on --t-sheet. This
 * is where v2 does its logging, so it has to feel light: the scrim is a real
 * button (tap anywhere to dismiss) and Escape closes.
 *
 * Deviation from the template: the template positions the sheet `absolute`
 * inside its 430px phone frame. In the app the sheet is `fixed` so it covers
 * the viewport regardless of scroll position.
 */
import { useEffect } from 'react';
import type { CSSProperties, ReactNode } from 'react';

interface FlowSheetProps {
  open?: boolean;
  title?: ReactNode;
  meta?: ReactNode;
  onClose?: () => void;
  footer?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
  'aria-label'?: string;
  'data-testid'?: string;
}

export function FlowSheet({
  open = true,
  title,
  meta,
  onClose,
  footer,
  children,
  style,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: FlowSheetProps) {
  // Escape closes. Bound on the document rather than the dialog so it works
  // before anything inside has taken focus.
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'flex-end',
        zIndex: 900,
      }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(6,5,4,.62)',
          backdropFilter: 'blur(3px)',
          border: 0,
          padding: 0,
          cursor: 'pointer',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        data-testid={testId}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 480,
          margin: '0 auto',
          maxHeight: '86%',
          background: 'var(--paper-1)',
          borderTop: '1px solid var(--edge-strong)',
          borderTopLeftRadius: 'var(--r-xl)',
          borderTopRightRadius: 'var(--r-xl)',
          boxShadow: 'var(--elev-3)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'v2-sheet-in var(--t-sheet) var(--ease-out)',
          overflow: 'hidden',
          ...style,
        }}
      >
        <div style={{ display: 'grid', placeItems: 'center', paddingTop: 10, flexShrink: 0 }}>
          <span
            style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--paper-4)' }}
            aria-hidden
          />
        </div>
        {(title || meta) && (
          <div style={{ padding: '12px 18px 10px', flexShrink: 0 }}>
            {title && (
              <h2
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'var(--ink-1)',
                }}
              >
                {title}
              </h2>
            )}
            {meta && (
              <p
                style={{
                  margin: '4px 0 0',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  color: 'var(--ink-3)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {meta}
              </p>
            )}
          </div>
        )}
        <div
          style={{
            padding: '0 18px',
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            style={{
              padding: 16,
              display: 'flex',
              gap: 10,
              borderTop: '1px solid var(--edge)',
              background: 'var(--paper-1)',
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
